/**
 * DRR-092: Legacy File Viewer Component
 *
 * In-browser rendering for legacy archive files. Renders content
 * based on MIME type without requiring download:
 * - PDF: Reuses existing PDFViewer (react-pdf)
 * - Images: Native <img> element
 * - Text/CSV/JSON: Mantine Code block (<pre>)
 * - Office formats: Not rendered — shows message to use ETL extracted preview
 *
 * Access gated by `canView` boolean from backend.
 * CSP sandbox header applied by CloudFront (disables scripts in content).
 */

import { useState, useEffect } from 'react';
import { Text, Alert, Code, Center, Image, Loader } from '@mantine/core';
import { IconEyeOff, IconFile } from '@tabler/icons-react';
import { PDFViewer } from '../pdf/PDFViewer';
import type { ScanStatus } from '@/generated/graphql';

interface LegacyFileViewerProps {
  viewUrl: string | null;
  fileName: string;
  contentType: string;
  scanStatus: ScanStatus;
  canView: boolean;
}

export function LegacyFileViewer({
  viewUrl,
  fileName,
  contentType,
  scanStatus,
  canView,
}: LegacyFileViewerProps) {
  // Blocked files: show status message only
  if (!canView || !viewUrl) {
    return (
      <Alert
        color={scanStatus === 'INFECTED' ? 'red' : 'gray'}
        icon={<IconEyeOff />}
        title={scanStatus === 'INFECTED' ? 'File Blocked' : 'Scan Pending'}
      >
        <Text size="sm">
          {scanStatus === 'INFECTED'
            ? 'This file was flagged as malware by ClamAV and cannot be viewed or downloaded.'
            : 'This file is queued for security scanning. It will be available after the scan completes.'}
        </Text>
      </Alert>
    );
  }

  // PDF: Use existing PDFViewer component (DRR-079)
  if (contentType === 'application/pdf') {
    return (
      <PDFViewer
        url={viewUrl}
        documentId=""
        annotations={[]}
        onAnnotationsChange={() => {}}
      />
    );
  }

  // Images: Native browser rendering
  if (contentType.startsWith('image/')) {
    return (
      <Center>
        <Image
          src={viewUrl}
          alt={fileName}
          maw={800}
          radius="sm"
          fallbackSrc={undefined}
        />
      </Center>
    );
  }

  // Text/CSV/JSON/Markdown: Code block
  if (
    contentType.startsWith('text/') ||
    contentType === 'application/json'
  ) {
    return (
      <TextFileViewer url={viewUrl} />
    );
  }

  // Office formats and others: Not renderable in-browser
  return (
    <Alert color="blue" icon={<IconFile />} title="Preview Not Available">
      <Text size="sm">
        This file type ({contentType}) cannot be previewed in the browser.
        Use the download button to save it to your device, or view the
        extracted text from the ETL output.
      </Text>
    </Alert>
  );
}

/**
 * Fetches and displays text file content in a code block.
 */
function TextFileViewer({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        setContent(text.slice(0, 100_000)); // 100KB preview limit
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [url]);

  if (loading) return <Center><Loader /></Center>;
  if (error) return <Alert color="red">Failed to load file: {error}</Alert>;

  return (
    <Code block style={{ maxHeight: '600px', overflow: 'auto' }}>
      {content}
    </Code>
  );
}
